import { propOr, prop, map, values, filter, complement, propEq, groupBy, flatten, pipe, sum } from 'ramda';

import { customMealCode } from 'types/meals';
import { types } from 'types/passengers';
import { isMoreThanXMinutesAgo } from 'utils/dates';
import { BOOKING_WINDOW_MINUTES } from 'constants/booking';

import groupByTariffCode from './groupByTariffCode';
import { isNoMealRoute } from 'utils/meals';
import { A, C, H, TA, TC, TW, W, E, EB, EH, EA, X, Y, Z } from 'constants/inventoryClass';

const isCancelled = propEq('cancelled', true);
const notCancelled = complement(isCancelled);
const validMealInventoryClasses = [A, H, W, C, TA, TW, TC, E, EB, EH, EA, X, Y, Z];
const invalidPassengerTypes = [types.INFANT, types.DOG];

const handleMealCodeLookup = ({ meals, passengerId, passengerType, inventoryClass, origin, destination }) => {
  const isValidPassengerType = !invalidPassengerTypes.includes(passengerType);
  const isValidClass = validMealInventoryClasses.includes(inventoryClass);
  const isValidRoute = !isNoMealRoute(origin, destination);

  if (isValidPassengerType && isValidClass && isValidRoute) {
    const meal = meals && meals.find(({ passenger: { id } }) => id === passengerId);

    if (meal) {
      return meal.code || customMealCode.REGULAR_MEAL;
    }
  }

  return customMealCode.NO_MEAL;
};

const Leg = {
  bookingItems: ({ meals, products, timing: { isoDate } }) =>
    products.map((product) => {
      const origin = product?.origin?.uic;
      const destination = product?.destination?.uic;
      const isDepartureTimeValid = isMoreThanXMinutesAgo(BOOKING_WINDOW_MINUTES)(isoDate);
      const isProductActive = !product.cancelled && isDepartureTimeValid;
      const isSeatEditable = Boolean(product.canChangeSeat && isProductActive && !product.isCheckedIn);
      const isMealEditable = Boolean(product.canChangeMeal && isProductActive && !product.isCheckedIn);
      const mealCode = handleMealCodeLookup({
        meals,
        passengerId: product.passenger.id,
        passengerType: product.passenger.type,
        inventoryClass: product.inventoryClass,
        origin,
        destination
      });

      return {
        ...product,
        isSeatEditable,
        isMealEditable,
        mealCode,
        __typename: 'BookingItem'
      };
    }),
  activeBookingItems: (leg) => pipe(Leg.bookingItems, filter(notCancelled))(leg),
  temporarilyCancelledBookingItems: (leg) =>
    pipe(
      Leg.bookingItems,
      filter((bookingItem) => bookingItem.cancelled && bookingItem.provisional)
    )(leg),
  productSummaries: (leg) =>
    pipe(
      Leg.activeBookingItems,
      groupBy((bookingItem) => `${bookingItem.passenger.type}-${bookingItem.currency}-${bookingItem.price}`),
      map(groupByTariffCode),
      values,
      flatten,
      map((obj) => ({ ...obj, __typename: 'ProductSummary' }))
    )(leg),
  refundSummaries: (leg) =>
    pipe(
      Leg.temporarilyCancelledBookingItems,
      groupBy((bookingItem) => bookingItem.passenger.type),
      map(groupByTariffCode),
      values,
      flatten,
      map((obj) => ({ ...obj, __typename: 'ProductSummary' }))
    )(leg),

  provisionalFees: pipe(propOr([], 'fees'), filter(propEq('provisional', true))),

  sumOfProvisionalFees: (leg) => pipe(Leg.provisionalFees, pipe(map(prop('price')), sum))(leg)
};

export default Leg;
